import { SelectOption } from '../purchase-order-detail/sections/section-base.types';
import {
  FH_BACKEND_TYPE_MAP,
  FH_BOOLEAN_OPTIONS,
  FH_FIELD,
  FH_FIELD_TYPE,
  FH_FIELD_VALIDATION,
  FH_FLAG,
  FH_PREDICATE_KEY_ALIASES,
  FH_SECTION_KIND,
  FH_SECTION_LABEL,
  FH_SERVICE_TYPE,
  FH_WARN,
  FhFieldType,
  FhFieldValidation,
  FhSectionKind,
  FhServiceType,
} from './company-form.constants';
import {
  CompaniesFormConfig,
  CompanyFieldConfig,
  CompanyFieldDescriptor,
  CompanyFormSection,
  FhDescriptorOrigin,
  FhPredicate,
  RawCompanyField,
  RawCompanyFieldMetadata,
  RawCompanyOption,
  RawCompanySection,
} from './company-form.types';

// Merges the static layout config (companies-form.config.json) with the per-country backend field
// config into an ordered list of renderable descriptors.
//
// Precedence: the backend owns `type`, `required`, `visible`, `options` and `regex` for every field
// it returns. A field the backend does not return still renders, falling back to the JSON
// `metadata`; with no metadata at all it defaults to a plain optional string. Everything the
// backend does not model (label, payload, dependencies, service token, under_input, min/max,
// accept, roles, default) always comes from the JSON.
//
// All functions are pure and free of Angular imports so they can be unit tested directly.
export class CompanyFieldFactory {

  static buildSections(
    json: CompaniesFormConfig,
    backendFields: CompanyFieldConfig[] | null,
  ): CompanyFormSection[] {
    const backendById = CompanyFieldFactory.indexBackendFields(backendFields);
    const usedControlNames = new Set<string>();
    const knownFieldIds = new Set<string>();

    const sections = (json?.sections ?? []).map((raw, index) =>
      CompanyFieldFactory.buildSection(raw, index, backendById, usedControlNames, knownFieldIds),
    );

    CompanyFieldFactory.warnUnmappedBackendFields(backendById, knownFieldIds);
    return sections;
  }

  static buildSection(
    raw: RawCompanySection,
    index: number,
    backendById: Map<string, CompanyFieldConfig>,
    usedControlNames: Set<string>,
    knownFieldIds: Set<string>,
  ): CompanyFormSection {
    const key = CompanyFieldFactory.sectionKey(raw.label, index);
    // Object.entries preserves insertion order for non-numeric keys, which is what gives us the
    // field order authored in the JSON.
    const fields = Object.entries(raw.fields ?? {}).map(([fieldId, rawField]) => {
      knownFieldIds.add(fieldId);
      return CompanyFieldFactory.buildDescriptor(
        fieldId, rawField, key, backendById, usedControlNames,
      );
    });

    return {
      key,
      label: raw.label,
      note: raw.note ?? null,
      visibleWhen: CompanyFieldFactory.normalizePredicate(raw.visible_when),
      kind: CompanyFieldFactory.resolveSectionKind(raw, fields),
      fields,
    };
  }

  static buildDescriptor(
    fieldId: string,
    raw: RawCompanyField,
    sectionKey: string,
    backendById: Map<string, CompanyFieldConfig>,
    usedControlNames: Set<string>,
  ): CompanyFieldDescriptor {
    const backend = backendById.get(fieldId);
    const meta = raw?.metadata;

    const type = CompanyFieldFactory.resolveType(backend, meta);
    const isSelect = type === FH_FIELD_TYPE.SELECT;
    const controlName = CompanyFieldFactory.resolveControlName(
      fieldId, raw?.payload, sectionKey, usedControlNames,
    );

    return {
      fieldId,
      controlName,
      sectionKey,
      label: raw?.label ?? fieldId,
      payload: raw?.payload ?? fieldId,
      type,
      required: CompanyFieldFactory.resolveRequired(backend, meta),
      requiredWhen: CompanyFieldFactory.normalizePredicate(meta?.required_when),
      visible: backend ? backend.visible !== false : true,
      visibleWhen: CompanyFieldFactory.normalizePredicate(meta?.visible_when),
      options: CompanyFieldFactory.resolveOptions(type, backend, meta),
      service: CompanyFieldFactory.resolveService(meta),
      // The single place the "ignore regex for selects" rule is applied.
      regex: isSelect ? null : (backend?.regex ?? meta?.regex ?? null),
      validation: CompanyFieldFactory.resolveValidation(meta),
      min: CompanyFieldFactory.resolveNumber(meta?.min),
      max: CompanyFieldFactory.resolveNumber(meta?.max),
      decimals: CompanyFieldFactory.resolveNumber(meta?.decimals),
      multiple: CompanyFieldFactory.toBool(meta?.multiple),
      defaultValue: meta?.default ?? null,
      accept: meta?.accept ?? [],
      maxSizeMb: CompanyFieldFactory.resolveNumber(meta?.max_size_mb),
      underInputLabel: meta?.under_input?.label ?? null,
      visibleFor: meta?.visible_for ?? [],
      editableBy: meta?.editable_by ?? [],
      fullWidth: type === FH_FIELD_TYPE.TEXTAREA || type === FH_FIELD_TYPE.FILE,
      origin: CompanyFieldFactory.resolveOrigin(backend, meta),
      note: meta?.note ?? null,
      valuePayload: meta?.value_payload ?? null,
      placeholder: meta?.placeholder ?? null,
      component: meta?.component ?? null,
      overwritePayloadType: CompanyFieldFactory.normalizeType(meta?.overwrite_payload_type),
    };
  }

  static indexBackendFields(fields: CompanyFieldConfig[] | null): Map<string, CompanyFieldConfig> {
    const map = new Map<string, CompanyFieldConfig>();
    (fields ?? []).forEach(field => {
      if (field?.id) {
        map.set(field.id, field);
      }
    });
    return map;
  }

  // Never returns undefined — this is the explicit fix for the reference implementation's switch,
  // which fell through and pushed `undefined` into the rendered list.
  static resolveType(backend?: CompanyFieldConfig, meta?: RawCompanyFieldMetadata): FhFieldType {
    const fromBackend = CompanyFieldFactory.normalizeType(backend?.type);
    if (fromBackend) {
      return fromBackend;
    }
    const fromMeta = CompanyFieldFactory.normalizeType(meta?.type);
    if (fromMeta) {
      return fromMeta;
    }
    if (meta?.type) {
      console.warn(FH_WARN.UNKNOWN_FIELD_TYPE, meta.type);
    }
    return FH_FIELD_TYPE.STRING;
  }

  static normalizeType(raw?: string): FhFieldType | null {
    if (!raw) {
      return null;
    }
    return FH_BACKEND_TYPE_MAP[String(raw).toUpperCase()] ?? null;
  }

  static resolveRequired(backend?: CompanyFieldConfig, meta?: RawCompanyFieldMetadata): boolean {
    if (backend) {
      return !!backend.required;
    }
    return CompanyFieldFactory.toBool(meta?.required);
  }

  static resolveOptions(
    type: FhFieldType,
    backend?: CompanyFieldConfig,
    meta?: RawCompanyFieldMetadata,
  ): SelectOption[] {
    // A boolean field always renders as a Yes/No select — the JSON/backend never declare options
    // for it (see international_company in companies-form.config.json).
    if (type === FH_FIELD_TYPE.BOOLEAN) {
      return FH_BOOLEAN_OPTIONS;
    }
    // A select with no options anywhere is a valid state — it renders with an empty list.
    const raw = backend ? backend.options : meta?.options;
    return CompanyFieldFactory.toSelectOptions(raw);
  }

  static toSelectOptions(options?: RawCompanyOption[]): SelectOption[] {
    return (options ?? [])
      .filter(option => option && option.value !== undefined && option.value !== null)
      .map(option => ({ id: String(option.value), name: option.label ?? String(option.value) }));
  }

  static resolveService(meta?: RawCompanyFieldMetadata): FhServiceType | null {
    const type = meta?.service?.type;
    const known: string[] = [
      FH_SERVICE_TYPE.CONTACT, FH_SERVICE_TYPE.OWNER_SEARCH, FH_SERVICE_TYPE.COMPANY_GROUP,
      FH_SERVICE_TYPE.TYPE_OF_COMPANY,
    ];
    return type && known.indexOf(type) !== -1 ? (type as FhServiceType) : null;
  }

  static resolveValidation(meta?: RawCompanyFieldMetadata): FhFieldValidation | null {
    const value = meta?.validation;
    if (value === FH_FIELD_VALIDATION.EMAIL || value === FH_FIELD_VALIDATION.URL) {
      return value;
    }
    return null;
  }

  static resolveOrigin(backend?: CompanyFieldConfig, meta?: RawCompanyFieldMetadata): FhDescriptorOrigin {
    if (backend) {
      return 'backend';
    }
    return meta ? 'json' : 'fallback';
  }

  // The JSON keys are already unique after the asset was normalized, but two sections previously
  // both used "file". Keep the guard so a future edit degrades to a deduped control instead of
  // silently overwriting a sibling control.
  static resolveControlName(
    fieldId: string,
    payload: string | undefined,
    sectionKey: string,
    used: Set<string>,
  ): string {
    const candidates = [fieldId, payload, `${sectionKey}__${fieldId}`];
    for (const candidate of candidates) {
      if (candidate && !used.has(candidate)) {
        if (candidate !== fieldId) {
          console.warn(FH_WARN.DUPLICATE_CONTROL, fieldId, '->', candidate);
        }
        used.add(candidate);
        return candidate;
      }
    }
    const fallback = `${sectionKey}__${fieldId}__${used.size}`;
    console.warn(FH_WARN.DUPLICATE_CONTROL, fieldId, '->', fallback);
    used.add(fallback);
    return fallback;
  }

  static resolveSectionKind(raw: RawCompanySection, fields: CompanyFieldDescriptor[]): FhSectionKind {
    if (raw?.label === FH_SECTION_LABEL.AGENCY_REBATES) {
      return FH_SECTION_KIND.REBATES;
    }
    if (fields.length > 0 && fields.every(field => field.type === FH_FIELD_TYPE.FILE)) {
      return FH_SECTION_KIND.FILES;
    }
    return FH_SECTION_KIND.FIELDS;
  }

  static sectionKey(label: string, index: number): string {
    const slug = (label ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return slug || `section_${index}`;
  }

  // Rewrites predicate keys through the alias map so a config that still says `company_type`
  // resolves against `type_of_company` instead of silently matching nothing.
  static normalizePredicate(predicate?: FhPredicate): FhPredicate | null {
    if (!predicate) {
      return null;
    }
    const entries = Object.entries(predicate);
    if (!entries.length) {
      return null;
    }
    const normalized: FhPredicate = {};
    entries.forEach(([key, value]) => {
      normalized[FH_PREDICATE_KEY_ALIASES[key] ?? key] = value;
    });
    return normalized;
  }

  // The JSON encodes booleans as strings ("true" / "false" / "conditional"). "conditional" means
  // the field is only ever required through `required_when`, so it coerces to false here.
  static toBool(value?: string | boolean): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    return value === FH_FLAG.TRUE;
  }

  static resolveNumber(value?: number): number | null {
    return typeof value === 'number' && !isNaN(value) ? value : null;
  }

  // Unlike toBool(), this never collapses an unrecognised default to false: a BOOLEAN field with
  // no explicit `default` (or a garbage one) starts out unselected. `false` is a real answer
  // ("No"), not the same as "not yet answered" — see CompanyFormComponent._initialValue().
  static resolveBooleanDefault(defaultValue: unknown): boolean | null {
    if (defaultValue === true || defaultValue === FH_FLAG.TRUE) {
      return true;
    }
    if (defaultValue === false || defaultValue === FH_FLAG.FALSE) {
      return false;
    }
    return null;
  }

  // A backend field with no JSON entry has no label and no payload, so it can neither be rendered
  // nor sent. Warn loudly rather than dropping it silently.
  static warnUnmappedBackendFields(
    backendById: Map<string, CompanyFieldConfig>,
    knownFieldIds: Set<string>,
  ): void {
    backendById.forEach((_field, id) => {
      if (!knownFieldIds.has(id)) {
        console.warn(FH_WARN.UNKNOWN_BACKEND_FIELD, id);
      }
    });
  }

  // Convenience lookups used by the orchestrator.
  static indexByControlName(sections: CompanyFormSection[]): Map<string, CompanyFieldDescriptor> {
    const map = new Map<string, CompanyFieldDescriptor>();
    sections.forEach(section => section.fields.forEach(field => map.set(field.controlName, field)));
    return map;
  }

  static indexControlNameByFieldId(sections: CompanyFormSection[]): Map<string, string> {
    const map = new Map<string, string>();
    sections.forEach(section => section.fields.forEach(f => map.set(f.fieldId, f.controlName)));
    return map;
  }

  static isRebateField(field: CompanyFieldDescriptor): boolean {
    return field.fieldId === FH_FIELD.REBATE_CURRENCY
      || field.fieldId === FH_FIELD.REBATE_THRESHOLD
      || field.fieldId === FH_FIELD.REBATE_PERCENTAGE;
  }
}
